import { BuilderAgentTools, BackendTools } from '@cloud-native/agent-tools'
import builderControls from './builderControls'

export default {
  popup: {
    agg_total: 'Total points',
    na: 'N/A',
    paginationCounter: '{current} of {total}',
    paginationError: 'Failed to load feature. Please try again.'
  },
  navBar: {
    titleTooltip: 'Go to Workspace',
    title: 'CARTO Builder',
    titleMobile: 'Builder',
    updating: 'Autosaving map...',
    updated: 'Updated',
    share: 'Share',
    takeOver: 'Take over editing',
    sharePrivateTooltip: 'Private map',
    shareSharedTooltip: 'Shared map',
    sharePublicTooltip: 'Public map',
    shareDisabledTooltip: 'Give a title to this map before sharing it',
    publishTooltip: 'Publish updates',
    shareTooltip: 'Share map',
    autosaved: 'Autosaved:',
    lastChangesSaved: 'Last changes saved:',
    autosavedDisabled: {
      title: 'Autosaving disabled',
      msg: 'There are errors in one or more data sources. Please fix them to enable autosaving again.'
    },
    beingEditedBySomeone: 'Being edited by {user}',
    someoneFinishedEditing: '{user} just finished editing',
    skipToWidgets: 'Skip to widgets',
    skipToMap: 'Skip to map',
    skipToNextWidget: 'Skip to next widget',
    skipToLastWidget: 'Last widget',
    skipToNextParameter: 'Skip to next parameter',
    skipToLastParameter: 'Last parameter'
  },
  actions: {
    preview: 'Preview',
    edit: 'Edit',
    credentials: {
      remove: 'Remove credentials'
    },
    export: {
      title: 'Export data',
      noDatasetsTooltip: 'No current data available for export',
      noDatasetsTooltipPDF: 'Add data to this map before downloading it',
      success: 'Data exported successfully.',
      failure: 'Failed to export layer data'
    },
    versionHistory: {
      title: 'Version history'
    },
    duplicate: {
      title: 'Duplicate map',
      disabledTooltip: 'Give a title to this map before duplicating it',
      dialog: {
        title: 'Duplicate map',
        content:
          "Some <strong>data sources</strong> won't be successfully loaded on the new map, please check the reasons below:",
        conflictDetails: 'The following connections are not shared with you:',
        continue: 'Continue anyway',
        cancel: 'Cancel'
      },
      success: 'Map duplicated successfully',
      successWithLink: 'Map duplicated successfully, ',
      successLinkLabel: 'click here to view it'
    },
    delete: {
      title: 'Delete map'
    },
    exportImage: {
      title: 'Export map',
      dialogTitle: 'Export map as PNG',
      resolutionLabel: 'Choose a download resolution',
      resStandard: 'Standard',
      resHigh: 'High',
      resMaximum: 'Maximum',
      exportButton: 'Export map',
      exporting: 'Exporting map…',
      errorOom: "Couldn't render at {tier} resolution.",
      retryAtLower: 'Retry at lower resolution',
      errorGeneric: 'Export failed.',
      errorReadinessTimeout: "Map isn't ready yet. Try again.",
      retry: 'Retry'
    },
    myLocation: {
      tooltip: 'Show your current location',
      loading: 'Obtaining your location...',
      unavailable: 'Location unavailable',
      noPermission:
        "CARTO doesn't have permission to use your location. You can change the permissions in your browser settings."
    },
    sharingOptions: 'Sharing options',
    publishUpdates: 'Publish updates',
    copy: 'Copy',
    copyLink: 'Copy link',
    linkCopiedToClipboard: 'Link copied to clipboard!',
    copiedToClipboard: 'Copied to clipboard'
  },
  exportDataDialog: {
    title: 'Export data',
    prompt:
      'Choose the data to download.{newline}{newline}Exports respect your current map and filters where supported. Learn more in our <docLink>documentation</docLink>',
    limitNote:
      'There is a limit of {limit} sources simultaneous export.{newline}Refer to our documentation to understand export limitations.',
    sources: 'Sources',
    fileFormat: 'File format',
    singleFormatTooltip: 'This is the only available format',
    limitTooltip: 'Max {limit} items per export.{newline} Deselect others to add this one.',
    exportData: 'Export data',
    settingsNote:
      'Manage which sources and columns can be exported{newline}and in which formats in <settingsLink>Export Data Settings</settingsLink>.',
    settingsNoteOld: 'Manage which sources can be exported in <settingsLink>Export Data Settings</settingsLink>.'
  },
  exportDataSettings: {
    title: 'Export data settings',
    prompt:
      'Manage which sources and columns can be exported and in which formats. Learn more in our <docLink>documentation</docLink>',
    promptOld: 'Manage which sources can be exported. Learn more in our <docLink>documentation</docLink>',
    sources: 'Sources',
    columns: 'Columns',
    format: 'Format',
    columnsPlaceholder: 'Select at least one column',
    formatsPlaceholder: 'Select a format',
    sourceWithoutLayer: 'This source isn’t linked to a layer. Only{newline}CSV and Parquet are available.',
    sourceWithSpatialIndex: 'This source uses a spatial index. Only{newline}CSV and Parquet are available.',
    noGeoColumn: 'This source doesn’t have geometry. Only{newline}CSV and Parquet are available.',
    notAvailableTileset: 'Tileset data cannot be exported',
    notAvailableRaster: 'Raster data cannot be exported',
    notAvailable: '{provider} data cannot be exported',
    save: 'Save',
    savedNotification: 'Settings saved successfully',
    leaveConfirm: {
      title: 'Are you sure you want to leave?',
      message: 'The changes you made will not be saved.'
    }
  },
  exportPDF: {
    title: 'Download Report',
    commentsDialog: {
      label: 'Comments',
      placeholder: 'Add a comment to the report',
      description: 'Do you want to add some comments to the PDF report?',
      cancel: 'Cancel',
      preview: 'Preview'
    },
    previewDialog: {
      backToBuilder: 'Back to Builder',
      backToMap: 'Back to map',
      download: 'Download Report',
      title: 'Title',
      date: 'Report date',
      comments: 'Comments',
      description: 'Description',
      legend: 'Legend',
      parameters: 'Parameters',
      values: 'Values',
      selectedValue: 'Selected value',
      selectedValues: 'Selected values',
      startDate: 'Start date',
      endDate: 'End date',
      min: 'Min',
      max: 'Max',
      widgets: 'Widgets',
      print: 'Print',
      nTableColumnsDisplayed: '{n} of {total} columns displayed',
      nTableRows: 'Rows 1-{n} of {total}'
    },
    screenshot: {
      loading: 'Loading screenshot...'
    }
  },
  mobileModal: {
    title: 'Are you on mobile?',
    textCapabilities: 'CARTO Builder® currently has limited map edition capabilities on mobile and tablet devices.',
    textResolution: 'If you are already on a desktop device, please try a bigger window size or higher resolution.',
    notShowAgain: 'Do not show again',
    accept: 'Understood'
  },
  settingsGroup: {
    hideAdvanced: 'hide advanced',
    showAdvanced: 'show advanced',
    addSection: 'add section',
    removeSection: 'remove section'
  },
  layerPanel: {
    emptyLayers: 'When a layer is added, it will be displayed here.',
    loadingDataSources: 'Waiting for data source to show map layers',
    dataClassificationMethod: 'Data classification method',
    scaleOrdinalDynamic: 'Ordinal (dynamic)',
    scaleCustom: 'Custom',
    dataClassificationInfo: 'To modify the colors and steps number, adjust them in the palette field above.',
    values: 'values',
    picker: 'Color picker',
    preset: 'Preset colors',
    aggregateByGeometry: 'Aggregate by geometry',
    aggregateByGeometryTooltip:
      'Aggregate your layer by identical geometries. Properties will be aggregated for styling and interactions.',
    cartoColors: {
      title: 'CARTOColors',
      raster: 'Raster palettes',
      organization: 'Organization palettes',
      colorDeletedFromOrganization:
        "The palette is no longer accessible. Contact your organization's Admin to restore access.",
      others: 'Other palettes',
      learnMore: 'Learn more'
    },
    customMarkers: {
      markerFilePlaceholder: 'E.g.: file.svg, file.png',
      selectFromLibrary: 'Select from library',
      organizationLibrary: 'Organization markers library',
      cartoLibrary: 'CARTO markers library',
      recent: 'Recent',
      searchPlaceholder: 'Search markers',
      uploadAria: 'Upload marker',
      filterAria: 'Filter by library',
      filterAll: 'All',
      filterOrganization: 'Organization markers',
      filterCarto: 'CARTO markers',
      resultsCount: '{count, plural, one {# result} other {# results}} by "{query}"',
      noResultsTitle: 'No markers were found',
      noResultsDescription: "Try adjusting your search to find the markers you're looking for, or adjust your filters.",
      showWholeLibrary: 'Show the whole marker library',
      uploadMarker: 'Upload Marker',
      imageResolutionExceededError: 'The file is too large. Allowed maximum resolution is {size}x{size}px',
      fileTooLargeError: 'Allowed maximum size is {size}',
      changeIcon: 'Change icon'
    },
    hexColumnConfig: {
      label: 'Color Field',
      help: "Apply a color based on Hex code column's values"
    },
    channelSelector: {
      noSuitableFields: 'No suitable columns in dataset. Supported types: {types}.'
    },

    visualization: 'Visualization',
    advancedVisualizationOptions: 'Advanced visualization options',
    opacity: 'Opacity',

    fill: 'Fill',
    advancedFillOptions: 'Advanced fill options',
    addFill: 'Add fill',
    removeFill: 'Remove fill',
    color: 'Color',
    palette: 'Palette',
    colorSchemeBy: 'Color scheme by',
    colorScale: 'Color scale',
    solidFill: 'Solid fill',
    patternFill: 'Pattern fill',
    patternLabel: 'Pattern',
    patternScale: 'Pattern scale',
    patternBy: 'Pattern by',
    patternDensity: 'Pattern density',
    pattern: {
      hlines: 'Horizontal lines',
      vlines: 'Vertical lines',
      diagLeft: 'Forward diagonal',
      diagRight: 'Backward diagonal',
      crossHatch: 'Cross-hatch',
      dots: 'Dots',
      checker: 'Checkerboard',
      none: 'No fill'
    },
    addValue: 'Add value',
    removeValue: 'Remove value',
    solid: 'Solid',
    simple: 'Simple',
    fixed: 'Fixed',

    stroke: 'Stroke',
    advancedStrokeOptions: 'Advanced stroke options',
    addStroke: 'Add stroke',
    removeStroke: 'Remove stroke',
    strokeColor: 'Stroke color',
    strokeWeight: 'Stroke weight',
    weightRangeBy: 'Weight range by',
    weightScale: 'Weight scale',
    strokeStyle: 'Stroke style',
    strokeStyleBy: 'Stroke style by',
    strokeStyleSolid: 'Solid',
    strokeStyleDashed: 'Dashed',
    strokeStyleDotted: 'Dotted',
    strokeStyleDash: 'dash',
    strokeStyleDot: 'dot',
    strokeStyleGap: 'gap',
    point: 'Point',
    customMarker: 'Custom marker',

    height: 'Height',
    advancedHeightOptions: 'Advanced height options',
    addHeight: 'Add height',
    removeHeight: 'Remove height',
    heightBasedOn: 'Height based on',
    heightScale: 'Height scale',
    showWireframe: 'Show wireframe',
    activate3dAlert: 'Active 3D view on the “Map View” toolbar above the map.',

    labels: 'Labels',
    advancedLabelsOptions: 'Advanced labels options',
    addLabels: 'Add labels',
    removeLabels: 'Remove labels',
    single: 'Single',
    double: 'Double',
    text: 'Text',
    textColor: 'Text color',
    haloColor: 'Halo color',
    mainText: 'Main text',
    mainHaloColor: 'Main halo color',
    secondaryText: 'Secondary text',
    secondaryHaloColor: 'Secondary halo color',
    placement: 'Placement',
    uniqueIdField: 'Unique Id',

    symbol: 'Symbol',
    advancedSymbolOptions: 'Advanced symbol options',
    marker: 'Marker',
    multipleMarkersBy: 'Multiple markers by',
    size: 'Size',
    radius: 'Radius',
    radiusRangeBy: 'Radius range by',
    rotation: 'Rotation',
    none: 'None',
    markerRotationBy: 'Marker rotation by',
    scaleWithZoomLevel: 'Scale with zoom level',
    baseSizeAtZoom: 'Base size at zoom level {zoom}',
    minMaxBounds: 'Min / Max bounds',

    cell: 'Cell',
    aggregationSize: 'Aggregation size',
    aggregationSizeTooltip: {
      spatialIndex:
        'Adjust the level of detail for spatial index aggregation. Higher values provide more detailed aggregation.',
      cluster: 'Adjust the level of detail for cluster aggregation. Lower values result in more granular clustering.'
    },

    areaOfInfluence: 'Area of influence',
    advancedAreaOfInfluenceOptions: 'Advanced area of influence options',
    radiusTooltip: 'Increasing radius smooths the heatmap but reduces detail',
    weight: 'Weight',
    weightBy: 'Weight by',
    uniform: 'Uniform',
    uniformSubtitle: 'All points are weighted equally',

    style: 'Style',
    uniqueValuesDisabled: 'Not supported for rasters lacking discrete numeric bands',
    restoreStyle: 'Restore style',
    styleToolip: 'Advanced style options',
    colorRange: 'Color range',
    unique: 'Unique',
    rgb: 'RGB',
    customAggregationPlaceholder: 'Type your SQL expression',
    customAggregationTitle: 'SQL expression',
    customAggregationOption: 'Custom aggregation',
    customAggregationEmptyError: 'Expression cannot be empty',
    customAggregationUnbalancedParens: 'Unbalanced parentheses',
    customAggregationTooLong: 'Expression exceeds the maximum allowed length',
    customAggregationNonNumericError: 'Only numeric values are supported for styling',
    customAggregationMinLabel: 'Min',
    customAggregationMaxLabel: 'Max',
    customAggregationValueLabel: 'Value',
    customAggregationRangeLabel: 'Range',
    addCustomAggregationDialogTitle: 'Add custom aggregation',
    editCustomAggregationDialogTitle: 'Edit custom aggregation',
    customAggregationDiscardTitle: 'The aggregation is not saved yet',
    customAggregationDiscardBody: 'Are you sure you want to close? The custom aggregation will not be saved.',
    customAggregationDiscardConfirm: 'Yes, close',
    numericScaleRangeError: 'Ensure the ranges are continuous and correctly ordered.'
  },
  spatialData: {
    spatialType: 'Spatial type',
    spatialColumn: 'Spatial column',
    spatialColumnPlaceholder: 'Select a column',
    noAssociatedLayer: 'This source has no associated layer',
    infoAlert: {
      title: 'Define spatial data',
      message: 'To view the layer, please select a valid spatial column and type.'
    },
    errorHelperText: 'Select a valid column and type.',
    errorAlert: {
      title: 'Invalid spatial data',
      message: 'Please select a valid spatial data column and type to resolve this error.'
    },
    applySelection: 'Apply selection',
    learnMore: 'Learn more about spatial data in our <docLink>Documentation</docLink>'
  },
  layerPanelHeader: {
    actionsTooltip: 'More options',
    hideLayerTooltip: 'Hide layer',
    showLayerTooltip: 'Show layer',
    actions: {
      style: 'Layer style',
      duplicate: 'Duplicate',
      rename: 'Rename',
      delete: 'Delete',
      showOnlyThis: 'Show only this',
      showAllLayers: 'Show all layers',
      zoomToLayer: 'Zoom to',
      moveToFront: 'Bring to front',
      moveForward: 'Bring forward',
      moveBackward: 'Send backward',
      moveToBack: 'Send to back'
    },
    disabledTooltips: {
      zoomToLayer: 'Not supported for this layer'
    },
    errorDetailsTooltip: 'Show error details',
    selected: {
      back: 'Back',
      title: 'Layer style'
    },
    layerVisibility: {
      title: 'Visibility by zoom level',
      tooltip: 'This layer will only be visible in the selected zoom levels'
    },
    type: {
      line: 'line',
      point: 'point',
      polygon: 'polygon',
      grid: 'grid',
      h3: 'h3',
      tileset: 'tileset',
      heatmapTile: 'heatmap',
      clusterTile: 'cluster'
    },
    spatialIndexInfo: {
      title:
        "You're working with a {actionLink}, data properties need to be aggregated to be used in the map, interactions and widgets.",
      grid: 'grid',
      notShow: "Don't show me again"
    },
    aggregated: 'Aggregated',

    UNKNOWN: 'UNKNOWN',
    ERROR: 'ERROR',
    unknown: 'Unknown',
    openToFix: 'Open to fix',
    openToDefine: 'Open to define',
    fixOnSqlEditor: 'Fix on SQL Editor'
  },
  datasetLabels: {
    tileset: 'Tileset',
    geom: 'Geometry',
    h3: 'H3',
    quadbin: 'Quadbin',
    raster: 'Raster'
  },
  layerTypes: {
    tilesetPoint: {
      label: 'Point',
      title: 'Geometry without aggregation'
    },
    aggregatedTilesetPoint: {
      label: 'Point',
      title: 'Aggregated by geometry'
    },
    tilesetPolygon: {
      label: 'Polygon',
      title: 'Geometry without aggregation'
    },
    aggregatedTilesetPolygon: {
      label: 'Polygon',
      title: 'Aggregated by geometry'
    },
    tilesetLine: {
      label: 'Line',
      title: 'Geometry without aggregation'
    },
    aggregatedTilesetLine: {
      label: 'Line',
      title: 'Aggregated by geometry'
    },
    quadbin: {
      label: 'Grid',
      title: 'Aggregated into grid cells'
    },
    quadbinGeom: {
      label: 'Grid',
      title: 'Aggregated geometry into grid cells'
    },
    h3: {
      label: 'H3',
      title: 'Aggregated into hexagonal bins'
    },
    h3Geom: {
      label: 'H3',
      title: 'Aggregated geometry into hexagonal bins'
    },
    heatmap: {
      label: 'Heatmap',
      title: 'Aggregated geometry by density'
    },
    cluster: {
      label: 'Cluster',
      title: 'Aggregated geometry into circles'
    },
    raster: {
      label: 'Raster',
      title: 'Raster with overviews'
    },
    rasterNoOverviews: {
      label: 'Raster',
      title: 'Raster without overviews'
    }
  },
  layerAggregateByGeometryConfirmDialog: {
    title: 'Aggregate by geometry',
    message:
      'You are about to change the visualization type of your layer to aggregate by identical geometries.\n\nPlease note that some map components associated with this layer source cannot be preserved and will be affected by this change:',
    alert: 'The following elements will be removed from the map'
  },
  layerTypeChangeConfirmDialog: {
    spatialDefinitionChange: {
      title: 'Spatial Definition Change',
      message:
        'You are about to change the spatial definition of your source. Please note that some map components associated with this source cannot be preserved and will be affected by this change:'
    },
    quadbin: {
      title: 'Aggregated grid layer',
      message:
        'You are about to change the visualization type of your layer to aggregated grid. Please note that some map components associated with this layer source cannot be preserved and will be affected by this change:'
    },
    h3: {
      title: 'Aggregated h3 layer',
      message:
        'You are about to change the visualization type of your layer to aggregated h3. Please note that some map components associated with this layer source cannot be preserved and will be affected by this change:'
    },
    tileset: {
      title: 'Point layer',
      message:
        'You are about to change the visualization type of your layer to point. Please note that some map components associated with this layer source cannot be preserved and will be affected by this change:'
    },
    heatmapTile: {
      title: 'Aggregated heatmap layer',
      message:
        'You are about to change the visualization type of your layer to aggregated heatmap. Please note that some map components associated with this layer source cannot be preserved and will be affected by this change:'
    },
    clusterTile: {
      title: 'Aggregated cluster layer',
      message:
        'You are about to change the visualization type of your layer to aggregated cluster. Please note that some map components associated with this layer source cannot be preserved and will be affected by this change:'
    },
    query: {
      title: 'Query this table',
      message: 'Visualizing your data as dynamic tileset will affect following map properties:'
    },
    alertHeader: 'The following elements will be removed from the map'
  },
  loaderSelector: {
    title: 'Sources',
    addSource: {
      tooltip: 'Add source from...'
    },
    sqlParameters: {
      tooltip: 'Create a SQL Parameter',
      tooltipDisabled: 'SQL Parameters are not supported for Databricks (Legacy) connections',
      tooltipDisabledLegacy: 'Add a SQL Query data source to enable SQL Parameters'
    },
    dataFreshness: 'Data freshness',
    refreshDataSources: 'Refresh data sources'
  },
  fieldSelector: {
    by: 'by',
    defaultPlaceholder: 'Select field...',
    defaultFixedLabel: 'Fixed'
  },
  bandExprSelector: {
    chooseOption: 'Choose option',
    customExpressions: 'Custom expressions',
    addExpression: 'Add expression',
    invalidSyntax: 'Invalid syntax',
    none: 'None',
    bandBy: 'band by',
    bands: 'Bands',
    bandLabels: {
      red: 'Red',
      green: 'Green',
      blue: 'Blue',
      alpha: 'Alpha'
    }
  },
  dataFreshness: {
    default: 'Default',
    minutes: '{number, plural, =0 {0 minutes} one {# minute} other {# minutes}}',
    hours: '{number, plural, =0 {0 hours} one {# hour} other {# hours}}'
  },

  dataFreshnessDialog: {
    title: 'Configure data sources freshness',
    prompt: 'Determine how up-to-date the data sources in your map are at its initial load',
    default: 'Default',
    defaultHelperText:
      'Default data freshness in Builder depends on the type of the data source and the specific data warehouse provider. <docLink>Learn more</docLink>',
    customLabel: 'Set data freshness to',
    label: 'Select freshness'
  },
  swapFooterDialog: {
    changeSource: 'Change source'
  },
  SwapSourceDialog: {
    title: 'Change data source',
    warnTitle: 'The new source can unset some aspects of the map.',
    warnContent:
      'By changing the map source, aspects such as widgets, SQL parameters, styles and interactions of the maps can be unset.'
  },
  swapConnectionDialog: {
    title: 'Change source connection'
  },
  deleteDatasetDialog: {
    title: 'Delete source',
    message: 'Are you sure you want to delete the following data source from this map? This action cannot be undone.',
    note: 'Deleting the source will remove all dependent layers and widgets. Other related components may also be affected.',
    cancel: 'Cancel',
    delete: 'Yes, delete'
  },
  deleteAgentDialog: {
    title: 'Delete agent',
    message: 'Are you sure you want to delete this agent? This action cannot be undone.',
    cancel: 'Cancel',
    delete: 'Yes, delete'
  },
  deleteLayerDialog: {
    title: 'Delete layer',
    message: 'Are you sure you want to delete {layerName}? This action cannot be undone.',
    warning: 'The following elements will be removed from the map:',
    note: 'Its associated source ({dataSourceName}) will remain available.',
    cancel: 'Cancel',
    delete: 'Yes, delete'
  },
  sqlPanel: {
    title: 'SQL Editor',
    titleEdited: 'Edited',
    run: 'Run',
    stop: 'Stop',
    addSource: 'Add source',
    addSourceWithoutLayer: 'Add source without layer',
    updateSource: 'Update source',
    running: 'Running',
    ready: 'Ready',
    clearWidgetsConfirmationDialog: {
      title: 'Widgets applied',
      content: 'If you run the new query, all the widgets you applied on this dataset will be removed. Are you sure?'
    },
    buttonsLabels: {
      enterFullscreen: 'Enter full screen',
      exitFullscreen: 'Exit full screen',
      maximize: 'Maximize',
      minimize: 'Minimize',
      close: 'Close',
      connection: 'Connection used',
      spatial: {
        geom: 'Geometry',
        h3: 'H3',
        quadbin: 'Quadbins'
      }
    },
    errorDialogTitle: 'SQL error',
    createTable: 'Create table from query',
    platformLimitError:
      'The result of this query exceeds the platform limit. As a way to load the data in the map, try applying a filter, a limit, or creating a table from your query.',
    queryTooLong:
      'This query is taking too long. As a way to load the data in the map, try applying a filter, a limit, or creating a table from your query.',
    invalidGeoColumn: 'The geospatial support selected and the one used in the query are different.',
    spatialPopper: {
      title: 'Spatial data type',
      suggestedAddon: '(suggested)',
      hint: 'Learn more about data types at <a>docs.carto.com</a>',
      selectIndex: 'Select {type} {suffix}',
      manualOverrideSuffix: `(manually)`
    },
    spatialTypePrefix: 'Spatial type:',
    spatialIndexSuggestedHint:
      'The spatial data type has been automatically selected based on your query. Please review before running.',
    spatialTypeAutoSelectTooltip:
      'The spatial type will be auto-selected based on your query. Redefine it if required in the layers panel after running.'
  },
  shareDialog: {
    agentConfiguredWithQuerySources:
      'Sharing a map publicly is not allowed with Agents using query sources capability. Please adjust your Agent to use MCP tools instead to unlock this',
    advance: {
      title: 'Developers and embedding',
      applicationDevelopment: 'Application development',
      mapEmbedding: 'Map embedding'
    },
    editorsCanEdit: '<role>Editors</role> can edit',
    customizeYourApp: "Customize your map's initial view with URL parameters. Learn more in our <a>documentation</a>.",
    copyDisabledTooltip: 'Share your map with others to enable this option',
    altTitle: 'Publish',
    publicOption: 'Public map',
    publicDesc: 'Visible to anyone with the public share link',
    link: 'Share Link',
    developers: 'Developers',
    copy: 'Copy public share link',
    copied: 'Copied!',
    share: 'Or share map',
    embedThisMap: 'Embed code',
    embedCopyAndPasteInfo:
      'Copy the HTML code below to embed the map. More details in our <docLink>documentation</docLink>',
    generatingHtml: 'Generating HTML ...',
    copyInput: 'Copy',
    mapID: 'Map ID',
    mapIDCopyAndPasteInfo: 'Learn how to develop an app from CARTO Builder in our <a>documentation</a>.',

    generatingMapId: 'Generating Map ID ...',
    mapToken: 'Map API Access Token',
    generatingToken: 'Generating Map Token ...',
    mapTokenCopyAndPasteInfo: 'Copy and paste this Map token into your public applications to use this map.',
    mapTokenDocs: 'How to use map token',
    nonPublicSuccessInfo:
      'Notice: The shared and embedded versions of this map (apps included) are no longer available. You can make it public at any time.',
    nonPublicSuccessInfoLink: 'Learn more',
    errorMessage: 'Oops! Something went wrong and was unable to complete your request.',
    protectWithPassword: 'Password protect',
    addPassword: 'Add password',
    usersGroupsSelector: {
      notFound: 'No users or groups matching your search criteria',
      notFoundOnlyUsers: 'No users matching your search criteria'
    }
  },
  toolBar: {
    invalidGeometry: 'Mask geometries must not have self-intersection',
    mapView: 'Map view',
    mapViewNormal: 'Normal view',
    mapView3d: '3D view',
    shape: 'Shape',
    closeLeftPanel: 'Close left panel',
    closeRightPanel: 'Close right panel',
    closeTopPanel: 'Close top panel',
    closeBottomPanel: 'Close bottom panel',
    measureTool: 'Measure distance',
    measureToolInfo: 'Click on the map to trace the line you want to measure. Double click or Esc to stop measuring',
    measureToolSelectUnit: 'Select a unit',
    measureTotalDistance: 'Total distance',
    dualMapTooltip: {
      enter: 'Switch a dual map view',
      exit: 'Exit dual map view'
    },
    mapTitle: {
      label: 'Map title',
      placeholder: 'type a title'
    },
    mapDescription: {
      label: 'Map description',
      placeholder: 'type a description',
      helper: 'Max 800 characters allowed'
    },
    visibilityShapesTooltip: {
      show: 'Show shapes',
      hide: 'Hide shapes'
    },
    rightPaneDisabledTooltip: 'No widget or SQL parameters added',
    rightPaneNoWidgetsTooltip: 'No widgets added',
    rightPaneWidgetsTooltip: {
      show: 'Show widgets',
      hide: 'Hide widgets'
    },
    rightPaneDescriptionTooltip: {
      show: 'Show map description',
      hide: 'Hide map description'
    },
    rightPaneWidgets: 'Widgets',
    rightPaneParameters: 'Parameters',
    rightPaneWidgetsAndParameters: 'Widgets and Parameters',
    rightPaneTabs: {
      widgets: 'Widgets',
      parameters: 'Parameters'
    }
  },
  splitViews: {
    right: 'right map',
    left: 'left map',
    top: 'top map',
    bottom: 'bottom map'
  },
  layerBlending: {
    title: 'Layer Blending',
    optionAdditive: 'Additive',
    optionNormal: 'None',
    optionSubtractive: 'Subtractive'
  },
  layerActions: {
    zoomToLayerError: 'Zoom to extent is not supported for this layer'
  },
  addSources: {
    title: 'Add source to map',
    dataExplorer: 'Data Explorer',
    tableOrTileset: 'Table / Tileset',
    customQuery: 'Custom SQL query',
    addCustomQuery: 'Add Custom SQL query',
    sql: 'SQL',
    typeYourQuery: 'Type your own query',
    importFile: 'Import file',
    addSourceToMap: 'Add source to map',
    connections: 'Connections',
    fromConnections: 'From your connection',
    fromDataObservatory: 'From Data Observatory',
    selectConnection: 'Select an available connection',
    noConnection: 'No connection configured yet',
    lockedConnection: 'The connection cannot be changed in this process',
    switchConnection: 'Switch connection',
    learnSpatialSql: 'Use the Analytics Toolbox',
    manageConnections: 'Manage Connections',
    loading: 'Loading data source',
    analyzing: 'Analyzing...',
    dataObservatory: {
      title: 'Data Observatory',
      accessIn: {
        disabled: {
          title: 'This sample is not available in Snowflake, Redshift or Postgres',
          description: 'Please select your CARTO Data Warehouse connection to add this sample to the map.'
        },
        request: {
          title: 'Access requested',
          description:
            'You will be able to add this subscription to the map using this connection as soon as your request has been fulfilled.'
        }
      }
    },
    button: {
      addSource: 'Add source',
      addSources: 'Add sources',
      addSourceWithoutLayer: 'Add source without layer',
      addSourcesWithoutLayer: 'Add sources without layer',
      addSourceFrom: 'Add source from...',
      openSqlEditor: 'Open SQL editor'
    },
    tooLarge: {
      title: 'The selected table is too large',
      link: 'create a tileset',
      message: "It'll be added as SQL query with a filter. You can also {actionLink} to visualize the whole table.",
      add: 'Add source as SQL query'
    },
    geoColumnNotFound: {
      title: "We couldn't find a geometry or spatial index column in this table",
      link: 'documentation',
      message: 'Check our {actionLink} to learn about supported geospatial data types.'
    },
    spatialIndex: {
      title: 'This table contains a {resource} index',
      link: 'Learn more',
      message: 'This kind of table can be optimized for better performance and reduced processing cost. {actionLink}'
    },
    optimizationAvailable: {
      title: "This table isn't optimized and can be slow.",
      link: 'Use Data Explorer to optimize it',
      message: 'Creating clusters/indexes based on geospatial data may improve performance. {actionLink}'
    },
    optimizationMandatory: {
      title: 'This table is not ready for visualization.',
      link: 'Use Data Explorer to prepare it',
      message: '{actionLink}'
    },
    rasterNotSupported: {
      title: 'This table contains raster data',
      link: 'Learn more',
      message: 'Raster tables are still not supported in this context'
    },
    rasterWithoutStats: {
      title: 'This raster lacks the necessary band statistics for map visualization in Builder'
    },
    sourceError: {
      title: "This source can't be added",
      message: 'Its details could not be loaded. Open it in Data Explorer to see more.'
    },
    uploadFiles: {
      button: 'Upload Files',
      formats: 'Upload {CSV}, {Json} or {GeoJSON}.',
      link: 'supported file formats.',
      dropFile: 'Drag & drop your file here',
      browse: 'Browse',
      progressFileName: 'File {name}',
      progressPercentage: '{percentage}%',
      notSupported: `File “{filename}” is not supported`
    },
    import: {
      changeFile: 'Edit details',
      importAndAddSource: 'Import and Add source',
      importingDescription:
        'This process might take a bit to complete, the data source will be added to the map as soon as it finishes.',
      importingError: 'An error ocurred.'
    },
    noSourcesAdded: 'No sources added yet',
    connectionNotShared: `This connection is not shared with all the editors so it can't be used in this map unless you modify this map's sharing settings.`
  },
  widgets: {
    title: 'Widgets',
    widgetsRegionLabel: 'Widgets panel',
    activeFilters: '{count, plural, one {# active filter} other {# active filters}}',
    clearAllFilters: 'Clear all',
    noWidgetsAdded: 'No widget added yet',
    newWidget: 'New widget',
    addWidget: 'Add widget',
    addWidgetDisabledReason: {
      tilesetOrRasterWithoutLayer:
        'Widgets are not available for Tileset and Raster sources without layers. Please add a layer to enable it.'
    },
    selectField: 'select a field',
    applyOperation: 'Apply operation',
    formula: 'Formula',
    category: 'Category',
    pie: 'Pie',
    histogram: 'Histogram',
    timeseries: 'Time Series',
    table: 'Table',
    range: 'Range',
    columnOptions: 'Data',
    selectColumns: 'Select columns',
    selectColumnsAll: 'All',
    selectColumnsNone: 'None',
    data: 'Data',
    metric: 'Metric',
    operation: 'Operation',
    sqlExpression: 'SQL Expression',
    customAggregation: 'Custom aggregation',
    customAggregationPlaceholder: 'AVG(column_a) / AVG(column_b) * 100',
    displayOptions: 'Display options',
    formatting: 'Formatting',
    note: 'Note',
    markdownNotePlaceholder: `This is an example of note.\n\nYou can write your text here. You can use *italic* or **bold** to highlight words.`,
    collapsible: 'Collapsible widget',
    crossFiltering: 'Cross-filtering',
    crossFilteringTooltip:
      'Enable cross-filtering to allow users to filter a widget and its related widgets and layers linked.',
    crossFilteringSelect: 'Filter scope',
    crossFilteringSingleSource: 'Single source',
    crossFilteringSingleSourceDescription: 'Filters only the data source connected to this widget',
    crossFilteringMultipleSources: 'Multiple sources',
    crossFilteringMultipleSourcesDescription:
      'Filters all data sources that use the same filtering property as this widget',
    crossFilteringMultipleSourcesDisabled:
      'Only one Range Widget can cross-filter a given property across multiple sources',
    crossFilteringColumn: 'Sources containing “{column}” prop.',
    autoCollapse: 'Automatically collapse when layers are not visible',
    autoCollapseNoLayers: 'Collapse when no layers are visible',
    typeANote: 'type a note',
    widgetType: 'Widget type',
    fullWidth: 'Full width',
    columnWidth: 'Column width',
    widgetOptions: 'Widget options',
    copyId: 'Copy widget ID',
    rename: 'Rename',
    deleteWidget: 'Delete widget',
    aggregationColumn: 'Aggregation Column',
    buckets: 'Buckets',
    noWidgets: 'No widget added',
    addDataSourceBeforeAdding: 'Please, add a compatible data source before adding a widget.',
    loadingDataSources: "Loading data sources, please wait until they're loaded to add a widget.",
    backToMap: 'Back to map',
    aggregateBy: 'Aggregate by',
    groupDateTypes: {
      year: 'year',
      month: 'month',
      week: 'week',
      day: 'day',
      hour: 'hour',
      minute: 'minute',
      second: 'second'
    },
    addColumn: 'Add column',
    timeSeries: {
      metricLabel: 'Metric label',
      metricLabelPlaceholder: 'Type a name',
      multipleSeries: 'Multiple series',
      multipleSeriesTooltip:
        'Add more series by splitting your metric based on a category field, or by adding further metrics.',
      splitBy: 'Split by',
      splitByNote: 'Up to 10 series will be automatically selected. These will be those with the dominant values.',
      addMetrics: 'Add metrics',
      allRemovedNote: 'Add up to 10 metrics.',
      metricLimitTooltip: 'Metrics limited to 10',
      interval: 'Interval',
      notAvailableTooltipOther: 'Only one Time Series widget per map is allowed',
      notAvailableTooltipNoTimeColumn: 'No valid timestamp column available',
      chartType: 'Style',
      chartTypeLine: 'Line',
      chartTypeBar: 'Bar'
    },
    playPause: 'Animation controls',
    filterByViewport: 'Filter by viewport',
    global: 'global',
    behavior: 'Behavior',
    behaviorNote:
      'Global widgets show values from the entire data source. Widgets filtered by viewport show values from the features located within the map extent.',
    animationWithGlobalMode: 'Animation controls are not available in global mode.',
    customMin: 'Custom min. value',
    customMax: 'Custom max. value',
    uniqueId: 'Unique ID',
    notAvailableTooltipRaster: '{widgetType} widget is not supported for raster sources',
    tableWidgetEmptyCase: 'No columns matching your search',
    tableWidgetSearchPlaceholder: 'Search in {widgetTitle}',
    tableWidgetSearch: 'Search',
    tableWidgetMultipleTitle: 'Data tables',
    noValue: 'No value',
    uniqueIdNote: 'A unique ID is required for precise widget calculations.',
    noData: 'No data available',
    noDataNote:
      'There are no results for the combination of filters applied to your data. Try tweaking your filters, or zoom and pan the map to adjust the Map View.',
    invalidColumnError: 'One or more columns used in this widget are not available in the data source.',
    rangeWidgetCustomLimitWarning: 'Custom limits can only be defined by one Range Widget per property',
    orderBy: {
      label: 'Order by',
      valuesDescending: 'Values descending',
      valuesAscending: 'Values ascending',
      alphabeticallyDescending: 'Alphabetically descending',
      alphabeticallyAscending: 'Alphabetically ascending'
    }
  },
  sqlParameters: {
    modifiedCount: '{count, plural, one {# modified parameter} other {# modified parameters}}',
    resetAll: 'Reset all',
    resetParameter: 'Reset parameter',
    copySqlName: 'Copy SQL name',
    copiedToClipboard: 'Copied to clipboard',
    clear: 'Clear',
    rename: 'Rename',
    edit: 'Edit parameter',
    remove: 'Remove control',
    values: 'Values',
    newValue: 'Add new value',
    addedValues: 'Added values',
    defaultValues: 'Default values',
    presetValues: 'Preset values',
    addCustomValues: {
      title: 'Add custom values',
      description: "Add values that aren't already in this parameter.",
      fieldLabel: 'Values',
      placeholder: 'e.g., Gotham, Metropolis',
      helperText: 'Separate multiple values with commas.',
      sessionAlert: "These values are only available during this session and won't be saved.",
      addButton: 'Add',
      cancelButton: 'Close',
      addValueTooltip: 'Add value',
      showAllValues: 'Show all values',
      duplicateWarning:
        '{count, plural, one {# value already exists} other {# values already exist}} and {count, plural, one {was} other {were}} skipped.'
    },
    allValues: 'All values',
    noValuesMatching: 'No values matching your search criteria.',
    editSuccessNotification: 'Changes saved successfully.',
    emptyResultError:
      'This parameter combination produces an empty result on {layerCount, plural, one {layer} other {layers}} {layers}. Please note that the displayed features are from the previous selection.',
    notUsedInfo:
      'This parameter is not being used in any data source. Add {placeholder} manually to at least one query to enable this control.',
    hints: {
      start: 'Start SQL name',
      end: 'End SQL name',
      generic: 'SQL name'
    },
    sourcesTooltip: 'Used on {count} data sources',
    noSourcesTooltip:
      'This parameter is not being used in any data source. Add its SQL name manually to at least one query for it to be applied.',
    text: {
      selectValue: 'Select value',
      customValue: 'Custom value',
      searchValue: 'Search value'
    },
    createDialog: {
      title: 'Create a SQL Parameter',
      titleEdit: 'Edit SQL Parameter',
      introPage: {
        msg: {
          1: 'SQL Parameters are placeholders that can be used on any SQL Query data source in Builder.',
          2: `After creating a new SQL Parameter, it needs to be added manually to one or more SQL Query data sources. Once added, the actual value for the parameter can be defined through a control UI in the right side panel's 'Parameters' tab.`,
          3: 'Learn more about SQL Parameters in our <link>Documentation</link>.'
        }
      },
      selectTypePage: {
        prompt: 'Select the type of parameter:',
        dates: {
          label: 'Date',
          description: 'Set a default date range users can adjust to filter your query.'
        },
        category: {
          label: 'Text',
          description: 'Define a list of values that users can select as string inputs in a query.'
        },
        numeric: {
          label: 'Numeric',
          descriptionNotYet: 'This type of parameter will be available soon.',
          description: 'Define a single value or a numeric range that users can select for use in a query.'
        }
      },
      categoryPage: {
        subtitle: 'Text parameter',
        prompt: 'Define a list of values that users can select as string inputs in a query.',
        selectionMode: {
          label: 'Selection mode',
          multi: 'Multiple values',
          single: 'Single value'
        },
        defaultPickerLabel: 'Default value',
        defaultTag: 'Default',
        emptyValueLabel: '(empty)',
        displayNamePlaceholder: 'Choose a name, e.g.: "Type of event"',
        sqlName: {
          label: 'SQL name',
          placeholder: "Choose a SQL name, like '{{'event_type}}",
          helpText:
            'Use the SQL name to place the parameter in your query. It will be replaced by an array of strings from the values picked in the selector. <link>Learn more</link>'
        },
        values: {
          manual: 'Add manually',
          fromSource: 'Add from a source',
          availableValues: 'Available values',
          valuePlaceholder: 'Type a value',
          noValues: 'No values added yet.',
          removeAll: 'Remove all',
          bottomHelpManual: 'These will be the values available in the parameter control.',
          bottomHelpFromDataSource:
            'Upload values from your source to be displayed in the parameter. You can add up to {maximumValues} values.',
          valuesCount: '{count, plural, =0 {0 values} one {# value} other {# values}} added',
          columnPlaceholder: 'Select the key column',
          searchValues: 'Search values',
          orderBy: {
            selectLabel: 'Order by',
            frequencyDesc: 'Frequency descending',
            frequencyAsc: 'Frequency ascending',
            alphabeticalDesc: 'Alphabetical descending',
            alphabeticalAsc: 'Alphabetical ascending'
          }
        }
      },
      datesPage: {
        subtitle: 'Date parameter',
        prompt: 'Set a default date range users can adjust to filter your query.',
        displayNamePlaceholder: 'Choose a name, e.g.: "Event Date"',
        sqlNames: {
          startLabel: 'Start date SQL name',
          startPlaceholder: "E.g.: '{{'date_from}}",
          endLabel: 'End date SQL name',
          endPlaceholder: `E.g.: '{{'date_to}}`,
          helpText:
            'Use the SQL name to place the parameters in your query. They will be replaced by dates from the values picked in the calendar. <link>Learn more</link>'
        },
        values: {
          startDateLabel: 'Start date',
          endDateLabel: 'End date',
          bottomHelp:
            'The values above will be used as default selections in the calendar. They do not restrict the selectable date range.'
        }
      },
      numeric: {
        subtitle: 'Numeric parameter',
        prompt: 'Define a single value or a numeric range that users can select for use in a query.',
        single: 'Simple slider',
        range: 'Range slider',
        displayNamePlaceholder: 'Chose a name, e.g.: "Buffer Size"',
        values: {
          label: 'Set values',
          minLabel: 'Min. value',
          defaultLabel: 'Default value',
          maxLabel: 'Max. value',
          defaultPlaceholder: 'Def.',
          minPlaceholder: 'E.g.: 0',
          valuePlaceholder: 'E.g.: 50',
          maxPlaceholder: 'E.g.: 100'
        },
        sqlName: {
          label: 'SQL name',
          placeholder: `Choose a SQL name, e.g.: '{{'buffer_size}}`,
          helpText:
            'Use the SQL name to place the parameter in your query. It will be replaced by an numeric value picked in the slider. <link>Learn more</link>'
        },
        sqlNames: {
          startLabel: 'Min SQL name',
          startPlaceholder: `E.g.: '{{'min_size}}`,
          endLabel: 'Max SQL name',
          endPlaceholder: `E.g.: '{{'max_size}}`,
          helpText:
            'Use the SQL name to place the parameter in your query. It will be replaced by an numeric value picked in the slider. <link>Learn more</link>'
        },
        scale: {
          label: 'Scale',
          continuous: 'Continuous',
          discrete: 'Discrete',
          step: 'Step increment',
          stepPlaceholder: 'E.g.: 5',
          stepHint: 'Interval between selectable values.'
        }
      },
      common: {
        names: 'Naming',
        values: 'Values',
        behavior: 'Behavior',
        displayName: 'Display name',
        createParameter: 'Create parameter'
      }
    },
    editDialog: {
      placeholdersChangedWarning: {
        title: 'Some SQL Query sources might be affected',
        message1:
          'When updating the "SQL name" of the parameter, ensure to update the linked SQL Query sources with the new placeholder:'
      }
    },
    removeDialog: {
      title: 'Remove SQL parameter control',
      msg: {
        1: 'The <strong>{name}</strong> parameter control will no longer be displayed.',
        2: 'You are going to remove the control for the parameter {name}.{br}The parameter will keep its current value in all queries where it has been utilized until it is manually removed from them.'
      },
      alert:
        'In order to actually delete the parameter from the source, please make sure you have removed all references to {placeholder} from all SQL queries.',
      confirmButton: 'Remove control'
    }
  },
  filterBar: {
    ariaLabel: 'SQL parameters',
    pillAriaLabel: '{name}, {value}',
    resetTooltip: 'Reset',
    resetShortcutHint: 'Reset {name}: press Backspace or Delete',
    carousel: {
      previous: 'Show previous parameters',
      next: 'Show next parameters'
    },
    value: {
      all: 'All',
      none: 'None',
      selectedCount: '{count} selected',
      range: '{start} – {end}'
    },
    numericValueLabel: 'Value',
    rangeValuesLabel: 'Range values',
    numericValueAriaLabel: '{name} value',
    rangeStartAriaLabel: '{name} range start',
    rangeEndAriaLabel: '{name} range end',
    numericSliderAriaLabel: '{name} value slider',
    rangeSliderStartAriaLabel: '{name} range start slider',
    rangeSliderEndAriaLabel: '{name} range end slider',
    dateRangeAriaLabel: '{name} date range',
    popoverAriaLabel: '{name} parameter',
    reorder: {
      handleAriaLabel: 'Reorder {name}',
      instructions:
        'Press Space or Enter to start reordering. Use the left and right arrow keys to move the parameter, Space or Enter to drop it, and Escape to cancel.',
      picked: 'Picked up {name}. It is in position {position} of {total}.',
      moved: 'Moved {name} to position {position} of {total}.',
      dropped: 'Dropped {name} in position {position} of {total}.',
      cancelled: 'Reordering cancelled. {name} returned to position {position} of {total}.'
    }
  },
  datasetItem: {
    providerPlaceholder: 'CARTO',
    records: `{records, plural, =0 {0 RECORDS} one {# RECORD} other {# RECORDS}}`,
    pixels: `{records, plural, =0 {0 PIXELS} one {# PIXEL} other {# PIXELS}}`,
    showDataTable: 'Show data table',
    openSql: 'Open SQL Editor',
    closeSql: 'Close SQL Editor',
    addLayer: 'Add layer',
    deleteSource: 'Delete source',
    loadingSource: 'Loading source...',
    showError: 'Show error details',
    actionsTooltip: 'More options',
    sqlQueryApplied: 'SQL',
    sqlQueryAppliedTooltip: 'SQL query applied',
    error: 'Error',
    sqlWarning: 'SQL warning',
    cancelled: 'Cancelled',
    spatialIndexTooltip: 'This source contains a {index} index',
    workflowTooltip: 'Source generated from Workflows',
    rasterTooltip: 'This source contains associated Raster layer',
    noAssociatedLayerTooltip: 'This source has no associated layer',
    geometryTooltip: 'This source contains associated geometry layer',
    temp: 'Temp',
    tempTooltip:
      'Temporal table from CARTO Workflows. Make sure the table is persistent using the "Save as Table" component in Workflows.',
    rename: 'Rename',
    refreshDataset: 'Refresh data source',
    queryThisTable: 'Query this table',
    duplicate: 'Duplicate',
    changeDataSource: 'Change data source',
    changeSourceConnection: 'Change source connection',
    showInWorkflows: 'Show in Workflows',
    oauthErrorBQ:
      'We lost access to BigQuery. Please repair this connection using your Google credentials and refresh the map or data sources.',
    oauthErrorSnowflake:
      'We lost access to Snowflake. Please repair this connection using your Snowflake credentials and refresh the map or data sources.'
  },
  mapStyle: {
    layers: 'Map Layers'
  },
  dataTableModal: {
    title: 'Data table'
  },
  loading: {
    dataSources: 'Loading data sources…',
    tiles: 'Loading map tiles'
  },
  errors: {
    404: {
      title: 'Map not found',
      subtitle: 'The map you requested could not be found.'
    },
    mandatoryValue: 'This field is mandatory.',
    invalidSqlNameFormat: `Placeholders start with '{{'' and end with }}.`,
    invalidSqlNameChars: 'Use only alpha-numeric characters (a-z, 0-9 and _).',
    invalidSqlNameEmpty: `Enter name between '{{'' and }}.`,
    sqlParameterConflict: 'Name already used.',
    invalidDate: 'Invalid date, use YYYY-MM-DD format.',
    invalidNumber: 'Invalid number.',
    endBeforeStart: 'End must be after start.',
    minMaxError: 'Max must be greater than min.',
    valuesRequired: 'At least one value is required.',
    singleSelectRequired: 'Single-select mode requires exactly one default value selected.'
  },
  layerQuota: {
    notice: 'Consider hiding layers',
    message: 'Although you can keep showing more layers please keep in mind performance may be degraded.',
    noteText:
      'Depending on your computer’s capacity and the complexity of the layers, performance issues might appear. For a smoother experience we recommend working with {maxLayers} or less layers.'
  },
  builderAssistant: {
    title: 'Assistant',
    ariaLabel: 'Assistant panel',
    moreActions: 'More actions',
    deleteConversation: 'Delete conversation',
    deleteConfirm: {
      content: 'Are you sure you want to delete this conversation? This action cannot be undone.'
    },
    placeholder: 'Message Assistant...',
    sendBlocked: 'Wait for the map to finish loading to send',
    model: 'Model',
    modelChangeConfirm: {
      title: 'Do you want to switch the model?',
      content: "Changing the model will clear the current conversation. This action can't be undone.",
      confirm: 'Switch model'
    },
    welcome: {
      emptyMap:
        "Hi! I'm your Builder assistant. Tell me what you want to map and I'll build it — data, layers, styling, widgets and popups. To get started, describe:\\\n\\\n🗺️ The data you want to show — a table, a query, or addresses to map\\\n🎯 What the map should help people see or decide\\\n🎨 Any styling or widgets you already have in mind\\\n\\\n**What would you like to build?**",
      existingMap:
        "Hi! I'm your Builder assistant. I can restyle layers, add widgets and popups, set up filters, or help you refine this map. Tell me:\\\n\\\n🎯 What you want to change or improve\\\n🎨 The look or the interactions you're after\\\n❓ Or ask me why something isn't working\\\n\\\n**What would you like to do?**"
    }
  },
  sideBarRail: {
    label: 'Builder sections',
    assistant: 'Assistant',
    locked: 'The assistant is working. Wait for it to finish, or stop it in the message field.',
    collapse: 'Collapse panel',
    expand: 'Expand panel',
    shortcut: '⌘/Ctrl + B'
  },
  tabsPanels: {
    layer: 'Layers',
    widgets: 'Widgets',
    interaction: 'Interactions',
    map: 'Basemap',
    legend: 'Legend',
    ai: 'AI Agent for viewers'
  },
  baseMap: {
    carto: {
      title: 'CARTO'
    },
    googleMaps: {
      title: 'Google Maps'
    },
    custom: {
      title: 'Custom'
    },
    typeNotAvailable: "This basemap type is no longer accessible. Contact your organization's Admin to restore access.",
    styleNotAvailable: "The basemap is no longer accessible. Contact your organization's Admin to restore access."
  },
  legendManager: {
    title: 'Legend',
    noLayers: 'Add a layer before configuring a legend',
    hide: 'Remove layer from map layer list',
    show: 'Add layer to map layer list',
    hiddenLegendCaption: 'Legend hidden for this layer',
    hideLegend: 'Hide legend',
    showLegend: 'Show',
    hideLegendTooltip: "Hide this layer's legend",
    showLegendTooltip: "Show this layer's legend",
    entryType: {
      color: 'Color',
      strokeColor: 'Stroke color',
      colorBasedOn: 'Color based on',
      fillColorBasedOn: 'Fill color based on',
      strokeColorBasedOn: 'Stroke color based on',
      strokeStyleBasedOn: 'Stroke style based on',
      patternBasedOn: 'Pattern based on',
      fillPatternBasedOn: 'Fill pattern based on',
      patternColorBasedOn: 'Pattern color based on',
      radiusBasedOn: 'Radius range by',
      customMarkersBasedOn: 'Custom markers based on',
      rasterRgb: 'Raster bands combination'
    },
    changeLabel: 'Change text label',
    changeTitle: 'Change title',
    resetOverrides: 'Reset overrides',
    ordering: {
      value: 'Value',
      alphabetical: 'Alphabetical',
      valueTooltip: 'Order by value',
      alphabeticalTooltip: 'Order alphabetically',
      ascending: 'Ascending order',
      descending: 'Descending order'
    },
    baseMapSelector: 'Basemap selector',
    limit: 'Limited to {num} categories',
    zoom: {
      title: 'Zoom level {range}',
      tooltip: 'This layer is only visible at certain zoom levels'
    }
  },
  agent: {
    dialog: {
      title: 'Agent Configuration',
      alerts: {
        missingTool: 'Added MCP Tools are no longer active',
        missingToolContent: 'Review the Instructions section and remove any references to unavailable tools.',
        missingToolAction: 'Got it'
      },
      useCase: {
        label: 'Use Case',
        placeholder: 'Describe the use case this agent will support (e.g., identify cell towers affected by wildfires)',
        error: 'Complete the use case section to create the agent'
      },
      instructions: {
        label: 'Instructions',
        placeholder:
          'Define how the agent should behave: what it can help with, the types of questions users might ask, and how it should respond.',
        helperText: `Type '/' to mention tools or '$' for source properties. Markdown is supported.`,
        helperLinkText: 'Learn more',
        tooltip: 'Complete the instructions section to create the agent',
        toolCategories: {
          mcp: 'MCP',
          map: 'Core/Map',
          layer: 'Core/Layer',
          widgets: 'Core/Widgets',
          parameters: 'Core/Parameters',
          data: 'Core/Data',
          insights: 'Core/Insights',
          workflows: 'Core/Workflows'
        }
      },
      semanticModel: {
        label: 'Semantic model',
        import: 'Import YAML',
        placeholder: 'Define your semantic model',
        helperText: 'Learn more about semantic models for agents in our',
        helperLinkText: 'Documentation',
        error: 'Invalid YAML syntax. Check the',
        fileReadError: 'Failed to load YAML file'
      },
      tools: {
        label: 'MCP Tools',
        numberOfTools: '({numberOfTools})',
        searchPlaceholder: 'Search tool',
        noToolsAdded: 'No tools added yet',
        refreshTools: 'Refresh',
        inputs: 'Inputs',
        output: 'Output',
        reference: 'Tool reference',
        mcpTool: 'MCP Tool',
        helperText: 'Learn more about tools for agents in our {documentation}',
        documentation: 'Documentation',
        emptyCase: {
          title: 'MCP Tools',
          description: 'Workflows that have been configured as an AI tool will be displayed here',
          action: 'Go to Workflows'
        }
      },
      capabilities: {
        label: 'Capabilities',
        allowToQuerySources: 'Query sources for insights',
        allowToQuerySourcesDescription:
          'Ensure the data source connection grants only the necessary data access and permissions.',
        allowToQuerySourcesTooltip:
          "Allows the agent to run SQL queries via the map's connection, for analysis and creating new sources and layers",
        allowToQuerySourcesDisabledPublicMapTooltip:
          'Query sources is not available on Public Maps. Set your map to Private or Shared to unlock this capability'
      },
      model: {
        label: 'Model',
        placeholder: 'Select an LLM model',
        error: 'An LLM model is required for the AI agent to work.'
      },
      introduction: {
        label: 'Introduction message',
        welcome: 'Welcome message',
        welcomePlaceholder: 'Write the greeting users will see when they open the Agent',
        starters: 'Conversation starters',
        startersPlaceholder: 'Add a common prompt to start a conversation',
        startersAddButtonTooltip: 'Add prompt',
        startersRemoveButtonTooltip: 'Remove prompt',
        numberOfStarters: '({currentIntroductionStarters} of {maxIntroductionStarters})',
        starterTooLong: '{length}/{max} · Remove this starter to continue.',
        removeAll: 'Remove all'
      },
      actions: {
        save: 'Save',
        createAgent: 'Create Agent',
        close: 'Close',
        testAgent: 'Test in Preview',
        testAgentDisabledTooltip:
          'Activate the AI Agent option in "Map settings for viewers" to be able to test your agent.',
        testAgentDisabledMissingModelTooltip: 'Configure a valid model to test this agent.',
        testAgentDisabledIncorrectSemanticModelTooltip: 'Configure a valid semantic model to test this agent.',
        testAgentDisabledUseCaseTooltip: 'Complete the use case to test this agent.',
        testAgentDisabledIntroductionTooltip: 'Review the introduction to test this agent.',
        saveDisabledMissingModelTooltip: 'Configure a valid model to save this agent.',
        saveDisabledIncorrectSemanticModelTooltip: 'Configure a valid semantic model to save this agent.',
        saveDisabledIntroductionTooltip: 'Review the introduction to save this agent.',
        testAgentNotification: 'Preview',
        addTool: 'Add tool',
        showWorkflow: 'Show tool in Workflows',
        hideTools: 'Hide tools',
        addTools: 'Add tools',
        removeTool: 'Remove tool'
      },
      notifications: {
        agentCreated: 'Agent created. Preview your map to test it.'
      },
      confirmation: {
        agentNotCreated: 'AI Agent not created',
        unsavedChanges: 'Changes not saved',
        configurationLost: 'If you close the dialog now, your configuration will be lost.',
        unsavedChangesLost: 'If you close this dialog now, your unsaved changes will be lost.',
        cancel: 'Cancel',
        close: 'Leave without saving'
      },
      assistant: {
        title: 'Assistant',
        openTooltip: 'Assistant',
        closeTooltip: 'Hide',
        welcome:
          "Hi! I'm here to help you configure your Agent. To get started, describe:\\\n\\\n📝 The use case or problem to solve\\\n👥 Who will use the Agent\\\n🏁 What they should accomplish\\\n\\\n**What should your Agent help users do?**",
        welcomeExisting:
          "Hi! I'm here to help you improve your Agent.\\\n\\\nTell me about:\\\n🌿 What's not working as expected\\\n🎯 What you want to refine\\\n🔮 Changes to the agent's behavior\\\n\\\n**What would you like to change?**",
        placeholder: 'Message Assistant...',
        deleteConversation: 'Delete conversation',
        moreActions: 'More actions',
        modelChangeConfirm: {
          title: 'Switch model?',
          content: "Changing the model will clear the current conversation. This action can't be undone.",
          cancel: 'Cancel',
          confirm: 'Switch model'
        },
        changesReadyForReview: 'Changes ready for review.'
      }
    },
    panel: {
      title: 'AI Agent for viewers',
      alerts: {
        missingModel: 'The agent model is missing',
        missingModelAction: 'Select a model',
        missingTool: 'Added MCP Tools missing',
        missingToolAction: 'Review'
      },
      actions: {
        editAgent: 'Edit Agent',
        deleteAgent: 'Delete Agent'
      },
      emptyCase: {
        title: 'No Agent created yet',
        description: 'Enable users to get insights using natural language with an AI Agent',
        action: 'Create Agent',
        externalLinkAction: 'Tips for creating an AI agent'
      },
      configuration: {
        useCase: 'Use Case',
        model: 'Model',
        tools: 'MCP Tools',
        capabilities: 'Capabilities',
        noCapabilities: 'No capabilities selected',
        noTools: 'No tools added',
        querySourcesCapability: 'Query sources for insights'
      }
    },
    button: {
      title: 'AI Agent'
    },
    ariaLabel: 'AI Agent'
  },
  aiManager: {
    title: 'CARTO AI',
    agent: {
      title: 'AI Agent',
      description:
        'Allow users to interact with an AI agent that provides responses and insights about the map. <docLink>Learn more</docLink>',
      created: 'CARTO AI Agent created!',
      disabled: 'CARTO AI Agent disabled!',
      error: 'Error creating AI Agent'
    },
    mapContext: {
      title: 'Map context',
      tooltip: 'Configure the AI Assistant first to have this functionality on the map.',
      placeholder: 'Enter the core details of your map here to orient the AI'
    },
    conversation: {
      title: 'Conversation starters',
      placeholder: 'Type a suggested prompt',
      prompts: 'prompts'
    },
    asyncToolCard: {
      loading: 'Loading async tool',
      finished: 'Finished',
      finishedAriaLabel: 'Finished async tool',
      error: 'Error'
    },
    userGuide: {
      title: 'Add a User Guide',
      placeholder: 'Write an introductory message here'
    }
  },
  dynamicTiling: {
    outOfZoom: 'Some rows have been filtered at this zoom level. Zoom in to ensure you see all rows in the map.',
    tileTooLongTime:
      'Some parts of this map are taking longer than usual to load. Try zooming in for better performance.'
  },
  legendControl: {
    title: 'Layers',
    buttonTooltip: 'Show layer panel',
    basemap: 'Basemap selector'
  },
  passwordForm: {
    title: 'This map is protected with a password',
    placeholder: "Insert the map's password",
    iconTooltip: 'Toggle password visibility',
    button: 'Continue',
    contact: 'Contact the owner if you need to access this map',
    errors: {
      wrong: 'Wrong password',
      empty: 'Cannot be empty'
    }
  },
  searchAddress: {
    placeholder: 'Search location',
    cancelButton: 'Cancel search'
  },
  mapFloatingTools: {
    layout: 'Layout',
    tools: 'Tools',
    featureSelection: 'Feature selection',
    ariaLabel: 'Map tools'
  },
  layoutPopover: {
    group: {
      map: 'Map',
      widgets: 'Widgets'
    },
    columns: 'Columns',
    oneColumn: '1 column',
    twoColumns: '2 columns',
    floating: 'Floating',
    floatingDisabledTooltip: 'Turn off dual map view to float the widgets',
    dualMapView: 'Dual map view',
    resetLayout: 'Reset layout'
  },
  popupManager: {
    noLayers: 'Add a layer before configuring pop-ups',
    disabledTooltips: {
      heatmapTile: 'Interactions are not supported for Heatmap layer type.'
    },
    popupsOnLayer: {
      disabled: 'None',
      clickOnly: 'Click',
      hoverOnly: 'Hover',
      clickAndHover: 'Click + hover'
    },
    tabs: {
      click: 'Click',
      hover: 'Hover'
    },
    interactionSettingsRegionLabel: 'Layer interaction settings',
    clickOptions: {
      filterByFeature: 'Filter by feature',
      filterByFeatureTooltip: 'Filter data by selecting features on the map',
      onlyThisSource: 'Only this source',
      onlyThisSourceExtra: `Highlight the clicked features and filter the data of every layer and widget using this source.`,
      allLayers: 'All layers in the selected areas',
      allLayersExtra: `Highlight the clicked areas and filter the data of every layer and widget from the sources they overlap.`,
      allLayersDisabledPoint: `Point layers can't filter intersecting features`,
      allLayersDisabledLine: `Line layers can't filter intersecting features`,
      featureFilterUnsupportedLayerType: `Filter by feature can't be applied to {layerType} layers`,
      featureFilterUnsupportedStaticTileset: `Filter by feature can't be applied to static tilesets`,
      featureFilterUnsupportedAggregatedByGeometry: `Filter by feature can't be applied to layers aggregated by geometry`,
      layerTypeNames: {
        raster: 'Raster',
        heatmap: 'Heatmap',
        cluster: 'Cluster'
      },

      popupInfoPanel: 'Pop-up info panel'
    },
    fields: {
      empty: 'No field added yet',
      add: 'Add a field',
      addAll: 'Add all',
      fillAll: 'Fill all',
      removeAll: 'None',
      changeLabel: 'Change text label',
      resetOverrides: 'Reset overrides',
      selectedCount: '{count, plural, =1 {# field} other {# fields}}',
      filledCount: '{count} of {limit} fields',
      remove: 'Remove field',
      limitReached: 'Maximum of {limit} fields reached',
      allFieldsAdded: 'All available fields have been added',
      customAggregationsGroupTitle: 'Custom aggregations',
      addCustomAggregationOption: 'Add custom aggregation'
    },
    moreOptions: 'More options',
    styles: {
      none: 'None',
      light: 'Light',
      lightWithHiFirst: 'Light with highlighted 1st value',
      dark: 'Dark',
      darkWithHiFirst: 'Dark with highlighted 1st value',
      panel: 'Info panel',
      groupPopups: 'Pop-up window',
      groupSlider: 'Right side slider',
      selectInteraction: 'Select interaction'
    },
    disabledForEvent: 'No {event} pop-up applied',
    formatting: 'Formatting',
    featureDetails: 'Feature details',
    toList: 'Switch to standard mode',
    toHTML: 'Switch to HTML mode',
    template: {
      htmlEditor: 'HTML editor',
      save: 'Save',
      lostChangesWarning:
        'The custom HTML that is currently applied will be lost when switching back to the standard mode.',
      lostChangesContinue: 'Do you want to continue?',
      learnHowToCustomize: 'Learn how to customize your popups in our <link>Documentation</link>',
      invalidSql: 'Invalid SQL expression in template',
      invalidFieldCount:
        'The limit of 5 properties has been exceeded. Check the code and remove them until it reaches the allowed limit.',
      clickPopup: 'Click popup',
      hoverPopup: 'Hover popup'
    }
  },
  datasetSelector: {
    placeholder: 'Select a data source',
    noDatasets: "There aren't any valid sources in the map"
  },
  controls: {
    ...builderControls,
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    roundedZoom: 'Current rounded zoom value',
    basemap: 'Basemap selector',
    switchTo3d: 'Switch to 3D',
    switchTo2d: 'Switch to 2D'
  },
  settingsToggles: {
    controlCacheTime: 'Refresh data sources',
    exportViewportData: 'Export data',
    exportPDF: 'Download Report',
    comments: 'Comments',
    showMeasureDistanceTool: 'Measure tool',
    showTitlePublicMap: 'Show title in public map',
    addressSearchBar: 'Search location bar',
    sqlParameterControls: 'SQL parameters controls',
    showDateControl: 'Date control',
    scrollWheelZoom: 'Scroll wheel zoom',
    featureSelectionTool: 'Feature selection tool',
    reorderLayers: 'Reorder layers',
    showPerformanceWarnings: 'Show performance warnings',
    showMyLocationButton: 'Focus map on the current location'
  },
  mapSettings: {
    title: 'Map settings for viewers',
    ai: 'AI',
    interactivity: 'Interactivity',
    navigation: 'Navigation',
    display: 'Display',
    notifications: 'Notifications',
    basemaps: 'Basemaps',
    props: {
      agent: 'AI Agent',
      featureSelectionTool: 'Feature selection tool',
      reorderLayers: 'Reorder layers',
      sqlParameterControls: 'SQL parameters controls',
      exportViewportData: 'Export data',
      exportPDF: 'Download Report',
      exportMap: 'Export map',
      comments: 'Comments',
      showMeasureDistanceTool: 'Measure tool',
      showTitlePublicMap: 'Show title in public map',
      addressSearchBar: 'Search location bar',
      scrollWheelZoom: 'Scroll wheel zoom',
      showPerformanceWarnings: 'Show performance warnings',
      showMyLocationButton: 'Focus map on the current location',
      basemapsSelector: 'Basemap selector'
    },
    propsTooltips: {
      agent: 'Enable users to get insights using natural language with an AI Agent.',
      agentMissingModel: 'Select a model in the AI Agent tab to enable this setting',
      featureSelectionTool:
        'Enable this for user-driven data filtering with rectangle, circle, or custom polygon tools.',
      reorderLayers: 'Enable users to reorder layers using the map legend.',
      sqlParameterControls: 'Allow users to dynamically update data sources using parameter controls.',
      exportViewportData: 'Allow users to export data from the viewport extent as CSV format.',
      exportPDF: 'Allow users to download a static PDF report of the current map view.',
      exportMap: 'Allow users to export the current map view as a high-resolution PNG.',
      comments:
        'Allow viewers with access to the map to add and view comments. Mention others using @ and their email. Not available on public maps.',
      showMeasureDistanceTool: 'Allow users to measure point-to-point distances on the map.',
      showTitlePublicMap: 'Display the title header in public or embed map.',
      addressSearchBar: 'The search location bar allow viewers to quickly find places using addresses or coordinates.',
      scrollWheelZoom: 'Let users zoom in and out using the mouse scroll wheel.',
      showPerformanceWarnings: 'Allow performance warning notifications in the application.',
      showMyLocationButton: 'Allow users to center the map on their current location.',
      basemapsSelector: 'Allow viewers to switch between basemaps'
    }
  },
  loginPrompt: {
    title: "Oops! You can't access this map",
    content:
      "Use your CARTO credentials or sign up to access this map. If the map exists and is shared with you, you'll be automatically redirected to it.",
    contentEmbedded:
      'Login or sign up using your CARTO credentials in a new window. Then, refresh this page to access the map.'
  },
  noPermissionsPrompt: {
    title: "Oops! You can't access this map",
    content:
      'It looks like this map is currently not shared with you. Please contact the map owner if you want to get access.'
  },
  ai: {
    title: 'AI Agent',
    loading: 'Loading',
    location: 'Location',
    locationRemoved: 'Location removed',
    locationsRemoved: '{count} locations removed',
    zoomTo: 'Zoom to',
    history: {
      open: 'Conversation history',
      panelLabel: 'Conversation history',
      listLabel: 'Conversations',
      reopenError: 'The conversation could not be opened. Please try again.',
      restoreNoMapState: 'No saved map for this conversation, nothing changed.',
      restoreMapIncomplete: "Conversation loaded. The map couldn't be fully loaded, so some elements may be missing.",
      renameError: 'The conversation could not be renamed. Please try again.',
      deleteError: 'The conversation could not be deleted. Please try again.',
      deletedNotification: 'Conversation deleted',
      actions: {
        menuAriaLabel: 'Conversation actions',
        rename: 'Rename conversation',
        deleteFromHistory: 'Delete from history',
        newConversation: 'New conversation'
      },
      deleteConfirm: {
        title: 'Delete from history',
        body: "Are you sure you want to delete this conversation? The conversation will no longer appear in your history and can't be reopened.",
        cancel: 'Cancel',
        confirm: 'Yes, delete'
      },
      switchConfirm: {
        title: 'Switch conversation',
        body: 'The Agent is still working on this conversation. If you switch now, the response in progress will be lost.',
        stay: 'No, stay',
        switch: 'Yes, switch'
      }
    },
    chat: {
      placeholder: 'Message AI Agent...',
      helpTitle: 'How can I help you?',
      disclaimer: 'Responses are AI-generated. Please verify key information.',
      unspecifiedError: 'Something went wrong. Please try again, or delete the current chat to start a new session.',
      messageNotSent: 'Your message was not sent.'
    },
    actions: {
      [BuilderAgentTools.SetMapCenterAndZoom]: { loading: 'Zooming to location…', success: 'Zoomed to location' },
      [BuilderAgentTools.SetMapCenterAndZoomToLayer]: {
        loading: 'Zooming to layer "{layerTitle}"…',
        success: 'Zoomed to layer, "{layerTitle}"'
      },
      [BuilderAgentTools.GetFormulaWidget]: {
        loading: 'Reading from widget, "{widgetTitle}"…',
        success: '"{widgetTitle}" widget read'
      },
      [BuilderAgentTools.GetCategoryWidget]: {
        loading: 'Reading from widget, "{widgetTitle}"…',
        success: '"{widgetTitle}" widget read'
      },
      [BuilderAgentTools.GetHistogramWidget]: {
        loading: 'Reading from widget, "{widgetTitle}"…',
        success: '"{widgetTitle}" widget read'
      },
      [BuilderAgentTools.GetTimeSeriesWidget]: {
        loading: 'Reading from widget, "{widgetTitle}"…',
        success: '"{widgetTitle}" widget read'
      },
      [BuilderAgentTools.FilterCategoryWidget]: {
        loading: 'Filtering widget, "{widgetTitle}"…',
        success: '"{widgetTitle}" widget filtered'
      },
      [BuilderAgentTools.FilterHistogramWidget]: {
        loading: 'Filtering widget, "{widgetTitle}"…',
        success: '"{widgetTitle}" widget filtered'
      },
      [BuilderAgentTools.FilterRangeWidget]: {
        loading: 'Filtering widget, "{widgetTitle}"…',
        success: '"{widgetTitle}" widget filtered'
      },
      [BuilderAgentTools.SetLayersVisibility]: {
        loading: 'Updating layer visibility…',
        success: 'Layer visibility updated'
      },
      [BuilderAgentTools.GetMapCoordinates]: { loading: 'Reading map view…', success: 'Map view read' },
      [BuilderAgentTools.GetSpatialFilter]: { loading: 'Reading area of interest…', success: 'Area of interest read' },
      [BuilderAgentTools.GetActiveFilters]: { loading: 'Reading active filters…', success: 'Active filters read' },
      [BuilderAgentTools.SetSpatialFilter]: { loading: 'Setting mask…', success: 'Mask set' },
      [BuilderAgentTools.SetSqlParameterText]: {
        loading: 'Setting parameter, "{paramTitle}"',
        success: '"{paramTitle}" parameter set'
      },
      [BuilderAgentTools.SetSqlParameterDateRange]: {
        loading: 'Setting parameter, "{paramTitle}"',
        success: '"{paramTitle}" parameter set'
      },
      [BuilderAgentTools.SetSqlParameterNumeric]: {
        loading: 'Setting parameter, "{paramTitle}"',
        success: '"{paramTitle}" parameter set'
      },
      [BuilderAgentTools.SetSqlParameterNumericRange]: {
        loading: 'Setting parameter, "{paramTitle}"',
        success: '"{paramTitle}" parameter set'
      },
      [BuilderAgentTools.AddSource]: { loading: 'Adding source…', success: 'Source added' },
      [BuilderAgentTools.RemoveSource]: { loading: 'Removing source…', success: 'Source removed' },
      [BuilderAgentTools.AddLayer]: { loading: 'Adding layer…', success: 'Layer added' },
      [BuilderAgentTools.RemoveLayer]: { loading: 'Removing layer…', success: 'Layer removed' },
      [BuilderAgentTools.SetLayerStyle]: { loading: 'Styling layer…', success: 'Layer styled' },
      [BuilderAgentTools.AddMarker]: { loading: 'Adding marker…', success: 'Marker added' },
      [BuilderAgentTools.AsyncWorkflowJobCheckResults]: { loading: 'Checking progress…', success: 'Results checked' },
      [BuilderAgentTools.AddSourceFromWorkflows]: { loading: 'Adding source…', success: 'Source added' },
      [BuilderAgentTools.RefreshDataSources]: {
        loading: 'Refreshing data sources…',
        success: 'Data sources refreshed'
      },

      // TODO: https://app.shortcut.com/cartoteam/story/525202/workflow-execution-tool-name-is-dynamic
      // This should be a Tool ID as many others, with the proper parameters
      sync_workflow: { loading: 'Running "{toolName}"…', success: '"{toolName}" completed' },

      [BackendTools.ExecuteQuery]: { loading: 'Running query…', success: 'Query executed' },
      [BackendTools.LdsGeocode]: { loading: 'Geocoding address…', success: 'Address geocoded' },
      [BackendTools.AsyncWorkflowJobGetResults]: { loading: 'Retrieving results…', success: 'Results retrieved' },
      [BackendTools.GenerateChart]: { loading: 'Generating chart…', success: 'Chart generated' },
      [BackendTools.GetSemanticModel]: { loading: 'Loading semantic model…', success: 'Semantic model loaded' }
    },
    quota: {
      threadLimit: 'Monthly quota exceeded at your organization account.',
      threadLimitShort: 'Monthly quota exceeded.',
      tokenLimit:
        'The conversation limit for this session has been reached. To start a new session, please delete the current chat.',
      threadLimitDialogHeader: 'Monthly quota exceeded',
      threadLimitDialogContent: `The CARTO AI Agent feature on shared maps has reached its monthly limit and will be disabled until the next
        period.

        The AI Agent quota resets monthly, with your organization’s access renewing on <strong>{date}</strong>.

        For more details, please contact your CARTO administrator or refer to our documentation
      `
    },
    contextMenu: {
      help: 'Help',
      helpDescription: 'Learn more about AI Agents in our Documentation',
      copy: 'Copy conversation',
      removeContext: 'Remove map context',
      delete: 'Delete conversation',
      moreActions: 'More actions'
    },
    deleteChat: 'Delete conversation'
  },
  comments: {
    unsplitMapDialog: {
      title: 'Exit dual map view',
      keepPrompt: 'Choose which map you want to keep:',
      keepLabels: {
        left: 'Left map',
        right: 'Right map',
        top: 'Top map',
        bottom: 'Bottom map'
      },
      alertTitle: 'Manage comments before discarding',
      prompt: 'The {panel} contains comments. Choose how to manage them before proceeding:',
      preserve: 'Preserve existing status',
      resolveAll: 'Resolve all comments',
      helperText: 'The comments will move while preserving their location.',
      confirm: 'Confirm'
    }
  },
  units: {
    kilometers: 'Kilometers',
    miles: 'Miles'
  },
  mapPreview: {
    staticPreviewTooltip: 'This is a static preview showing only the map. Other elements are not included.',
    errorAlertTitle: 'We couldn’t load the preview map data.',
    retryButtonText: 'Try again'
  },
  versionHistoryDialog: {
    title: 'Version History'
  },
  mapVersionsManager: {
    showFullDescription: 'Show full description',
    showVersionChanges: 'Show version changes',
    restoreThisVersion: 'Restore this version',
    editDescription: 'Edit description',
    addDescription: 'Add description',
    duplicateMap: 'Duplicate map',
    restoreVersionSuccess: 'Version restored successfully',
    restoreVersionError:
      'CARTO could not restore the selected version. Please try again, or contact us if the problem persists.',
    duplicateMapError:
      'CARTO could not duplicate a map from the selected version. Please try again, or contact us if the problem persists.',

    saveChangesSuccess: 'Changes saved successfully',
    newVersionSaved: 'New version saved successfully',
    confirmRestore: {
      title: 'Restore version',
      message: {
        1: 'You’re about to restore an older version without saving the changes of the current version. Save first if you want to keep them.',
        2: 'Are you sure you want to restore this version?'
      },
      confirmButton: 'Yes, restore'
    }
  },
  aggregationTypes: {
    count: 'COUNT',
    sum: 'SUM',
    avg: 'AVG',
    max: 'MAX',
    min: 'MIN',
    stdev: 'STDEV',
    variance: 'VARIANCE',
    mode: 'MODE',
    any_value: 'ANY VALUE'
  },
  colorRangePanel: {
    type: 'Type',
    steps: 'Steps',
    reversed: 'Reversed'
  },
  customAggregation: {
    validationError: 'Could not validate the expression. Please check your network connection and try again.',
    expressionError: 'Could not validate the expression. Check the columns and SQL syntax.'
  },
  layerGroups: {
    add: 'Add',
    addGroup: 'Add group',
    defaultGroupName: 'Group {n}',
    deleteGroup: 'Delete group',
    renameGroupAriaLabel: 'Rename group',
    hideGroup: 'Hide group',
    showGroup: 'Show group',
    treeAriaLabel: 'Layers tree',
    expandGroup: 'Expand group',
    collapseGroup: 'Collapse group',
    groupOptions: 'Group options for {name}',
    moreOptions: 'More options',
    menu: {
      addLayer: 'Add layer',
      createGroup: 'Create group',
      addToGroup: 'Add to group',
      moveToGroup: 'Move to group',
      removeFromGroup: 'Remove from group',
      renameGroupAction: 'Rename',
      collapseGroupAction: 'Collapse',
      expandGroupAction: 'Expand',
      zoomToGroup: 'Zoom to',
      showOnlyThisGroup: 'Show only this',
      showAllGroups: 'Show all groups'
    },
    legend: {
      treeAriaLabel: 'Layer legend',
      groupSubtitleHiddenAriaLabel: '{name}, hidden'
    }
  }
}
